import classNames from 'classnames'
import gsap from 'gsap'
import React, {useRef, useState} from 'react'
import {render} from 'storyblok-rich-text-react-renderer'

import {LogoNavy, LogoNavyInverted} from '@/assets/specify'
import {TNavigationRichMenu} from '@/common/types'
import {unwrapRenderParagraph} from '@/common/utils'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import Link from '@/components/Link'
import StoryblokImageV2 from '@/components/StoryblokImageV2/StoryblokImageV2'

import styles from './NavigationRichMenu.module.scss'

const NavigationRichMenu = ({
  logo,
  logolight,
  homeLink,
  items = [],
  btnLink,
  btnLabel,
  languages,
  isDark,
}: TNavigationRichMenu): JSX.Element => {
  const mobileSubmenusRefs = useRef<(HTMLDivElement | null)[]>([])
  const [isMenuOpen, setIsMenuOpen] = useState<boolean | null>(false)
  const [mobileSubmenuIndex, setMobileSubmenuIndex] = useState<number | null>(null)

  const storeMobileSubmenus = (el: HTMLDivElement | null, i: number) => {
    mobileSubmenusRefs.current[i] = el
  }

  const handleItemClick = (i: number) => {
    setMobileSubmenuIndex(i)
  }

  React.useEffect(() => {
    if (!isMenuOpen) {
      setMobileSubmenuIndex(null)
    }
  }, [isMenuOpen])

  React.useEffect(() => {
    if (mobileSubmenuIndex !== null && mobileSubmenuIndex >= 0) {
      gsap.set(mobileSubmenusRefs.current[mobileSubmenuIndex], {zIndex: 30})
    } else {
      gsap.set(mobileSubmenusRefs.current, {zIndex: 10})
    }
  }, [mobileSubmenuIndex])

  return (
    <section
      className={classNames(styles.menu, {[styles.open]: isMenuOpen}, {[styles.dark]: isDark})}
    >
      <nav className={styles.section}>
        <div className={styles.nav}>
          <Link link={homeLink} className={styles.logoLink}>
            {isDark && !isMenuOpen && logolight && logolight.filename !== '' ? (
              <StoryblokImageV2
                filename={logolight.filename}
                alt={logolight.alt}
                sizes={{sm: 180, unit: 'px'}}
                width='180'
                fixedWidth={180}
              />
            ) : isDark && isMenuOpen && logo && logo.filename !== '' ? (
              <StoryblokImageV2
                filename={logo.filename}
                alt={logo.alt}
                sizes={{sm: 180, unit: 'px'}}
                width='180'
                fixedWidth={180}
              />
            ) : isDark && !isMenuOpen ? (
              <LogoNavyInverted title='Home' style={{height: '100%'}} />
            ) : isDark && isMenuOpen ? (
              <LogoNavy title='Home' style={{height: '100%'}} />
            ) : (
              <LogoNavy title='Home' style={{height: '100%'}} />
            )}
          </Link>

          <div className={styles.items}>
            {items.map((item) => (
              <div key={item._uid} className={styles.item}>
                <p className={styles.itemlabel}>{item.label}</p>
                <svg width='18' height='18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='m4.5 6.75 4.5 4.5 4.5-4.5'
                    stroke='#221F61'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <div className={styles.dropdown}>
                  <div className={styles.innerdropdown}>
                    <div className={styles.innerdropdowncontent}>
                      <p className={styles.dropdownlabel}>{item.label}</p>
                      <div className={styles.innerdropdowncontentrow}>
                        {item.items &&
                          item.items.length > 0 &&
                          item.items.map((item) => {
                            if (item.component !== 'navigation-rich-submenu-list') return null
                            return (
                              <div key={item._uid} className={styles.innerdropdowncontentcol}>
                                {item.title && item.title !== '' && (
                                  <p className={styles.innerdropdowncontentcoltitle}>
                                    {item.title}
                                  </p>
                                )}
                                {item.items && item.items.length > 0 && (
                                  <ul className={styles.innerdropdowncontentcolul}>
                                    {item.items.map((item) => {
                                      return (
                                        <li key={item._uid}>
                                          <Link
                                            label={item.label}
                                            link={item.link}
                                            className={styles.mobilemenucollink}
                                          />
                                        </li>
                                      )
                                    })}
                                  </ul>
                                )}
                              </div>
                            )
                          })}
                      </div>
                    </div>

                    <div className={styles.innerdropdownad}>
                      {item.page &&
                        item.page.length > 0 &&
                        item.page.map((item) => {
                          if (item.component !== 'navigation-rich-submenu-page') return null
                          return (
                            <div key={item._uid} className={styles.innerdropdownadbutton}>
                              <Link link={item.link}>
                                <div className={styles.innerdropdownadbuttonrow}>
                                  <p className={styles.innerdropdownadbuttonlabel}>{item.title}</p>
                                  <svg
                                    width='24'
                                    height='25'
                                    viewBox='0 0 24 25'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                  >
                                    <path
                                      d='M6 12.5H18.5M18.5 12.5L12.5 6.5M18.5 12.5L12.5 18.5'
                                      stroke='black'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                  </svg>
                                </div>
                                <p className={styles.innerdropdownadbuttondesc}>
                                  {render(item.description, unwrapRenderParagraph)}
                                </p>
                              </Link>

                              <div className={styles.semisphere}>
                                <img src={'/semisphere3D.png'} alt='semi sphere illustration' />
                              </div>

                              {item.illustration && item.illustration.filename !== '' && (
                                <div className={styles.avatar}>
                                  <StoryblokImageV2
                                    filename={item.illustration.filename}
                                    alt={item.illustration.alt}
                                    className={styles.avatarimage}
                                  />
                                </div>
                              )}
                            </div>
                          )
                        })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.buttonWrapper}>
            {btnLink && btnLabel && (
              <Link label={btnLabel} link={btnLink} className={styles.button} />
            )}
          </div>

          {btnLink && btnLabel && languages && <hr className={styles.divider} />}

          {languages && (
            <div className={styles.desktopLangWrapper}>
              <LanguageSwitcher languages={languages} isLight={!!isDark} />
            </div>
          )}

          <button className={styles.toggleMobileMenu} onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? (
              <svg
                width='25'
                height='24'
                viewBox='0 0 25 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M7.18893 17.2426L12.4316 12M17.6742 6.75736L12.4316 12M12.4316 12L7.18893 6.75736M12.4316 12L17.6742 17.2426'
                  stroke='#221F61'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <rect x='6' y='6' width='12' height='2' fill='#221F61' />
                <rect x='6' y='16' width='12' height='2' fill='#221F61' />
                <rect x='6' y='11' width='12' height='2' fill='#221F61' />
              </svg>
            )}
          </button>
        </div>
      </nav>

      <div className={classNames(styles.mobileMenu, {[styles.open]: isMenuOpen})}>
        <div className={styles.mobileSubmenuContainer}>
          <div className={styles.mobileList}>
            {items.map((item, i) => (
              <button
                key={item._uid}
                className={styles.mobileListItem}
                onClick={() => handleItemClick(i)}
              >
                <p className={styles.mobileListItemLabel}>{item.label}</p>
                <svg width='18' height='18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='m4.5 6.75 4.5 4.5 4.5-4.5'
                    stroke='#221F61'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            ))}
          </div>

          {items.map((item, i) => {
            return (
              <div
                key={item._uid}
                className={styles.mobileSubmenu}
                ref={(el) => storeMobileSubmenus(el, i)}
              >
                <button className={styles.mobilemenutitlecol} onClick={() => handleItemClick(-1)}>
                  <svg width='18' height='18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='m4.5 6.75 4.5 4.5 4.5-4.5'
                      stroke='#221F61'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <p>{item.label}</p>
                </button>

                {item.items &&
                  item.items.length > 0 &&
                  item.items.map((item) => {
                    if (item.component !== 'navigation-rich-submenu-list') return null
                    return (
                      <div key={item._uid} className={styles.mobilemenucol}>
                        <p className={styles.mobilemenucoltitle}>{item.title}</p>
                        {item.items &&
                          item.items.length > 0 &&
                          item.items.map((item) => {
                            return (
                              <Link
                                key={item._uid}
                                label={item.label}
                                link={item.link}
                                className={styles.mobilemenucollink}
                              />
                            )
                          })}
                      </div>
                    )
                  })}

                {item.page &&
                  item.page.length > 0 &&
                  item.page.map((item) => {
                    if (item.component !== 'navigation-rich-submenu-page') return null
                    return (
                      <div key={item._uid} className={styles.mobilemenuad}>
                        <Link link={item.link} className={styles.mobilemenuadbutton}>
                          <div className={styles.mobilemenuadbuttonbuttonrow}>
                            <p className={styles.mobilemenuadbuttonbuttonlabel}>{item.title}</p>
                            <svg
                              width='24'
                              height='25'
                              viewBox='0 0 24 25'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M6 12.5H18.5M18.5 12.5L12.5 6.5M18.5 12.5L12.5 18.5'
                                stroke='black'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </div>
                          <p className={styles.mobilemenuadbuttonbuttondesc}>
                            {render(item.description, unwrapRenderParagraph)}
                          </p>
                        </Link>

                        <div className={styles.avatar}>
                          <img src={'/avatar.png'} alt='a.i.lice avatar' />
                        </div>
                      </div>
                    )
                  })}
              </div>
            )
          })}
        </div>
      </div>

      <div className={styles.stickybottomMobile}>
        <div className={styles.buttonWrapper}>
          {btnLink && btnLabel && (
            <Link label={btnLabel} link={btnLink} className={styles.button} />
          )}
        </div>

        {languages && (
          <div className={styles.desktopLangWrapper}>
            <LanguageSwitcher languages={languages} isLight={false} isTop={true} />
          </div>
        )}
      </div>
    </section>
  )
}

export default NavigationRichMenu
